/**
 * Help Center Page Loader
 * Pre-loads help articles and categories before rendering
 */

import type { LoaderFunctionArgs } from 'react-router-dom';
import { requireAuth, getSearchParams, loaderCache } from './helpers';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';

export interface HelpCenterData {
  categories: Array<{
    id: string;
    name: string;
    description: string;
    icon: string;
    articleCount: number;
    color: string;
  }>;
  articles: Array<{
    id: string;
    title: string;
    excerpt: string;
    category: string;
    readTime: number;
    views: number;
    helpful: number;
    lastUpdated: string;
    isPopular: boolean;
  }>;
  popularArticles: Array<{
    id: string;
    title: string;
    category: string;
    views: number;
  }>;
  recentUpdates: Array<{
    id: string;
    title: string;
    updatedAt: string;
  }>;
  faqs: Array<{
    id: string;
    question: string;
    answer: string;
    category: string;
    helpful: number;
  }>;
  searchSuggestions: string[];
  stats: {
    totalArticles: number;
    totalViews: number;
    averageRating: number;
    responseTime: string;
  };
}

/**
 * Fetch help center data from API
 */
async function fetchHelpCenterData(searchParams: URLSearchParams): Promise<HelpCenterData> {
  const cacheKey = `help-center-${searchParams.toString()}`;

  // Check cache first
  const cached = loaderCache.get<HelpCenterData>(cacheKey);
  if (cached) {
    return cached;
  }

  // TODO: Replace with actual API call
  // const query = new URLSearchParams(searchParams);
  // const response = await fetch(`${API_URL}/help-center?${query}`);
  // const data = await response.json();

  // Mock data for now
  const data: HelpCenterData = {
    categories: [
      {
        id: 'getting-started',
        name: 'Getting Started',
        description: 'Learn the basics of DomainBoost',
        icon: 'BookOpen',
        articleCount: 0,
        color: '#4F46E5',
      },
      {
        id: 'orders',
        name: 'Orders & Services',
        description: 'Managing your orders',
        icon: 'Package',
        articleCount: 0,
        color: '#059669',
      },
      {
        id: 'billing',
        name: 'Billing & Payment',
        description: 'Payment and invoices',
        icon: 'CreditCard',
        articleCount: 0,
        color: '#F59E0B',
      },
      {
        id: 'account',
        name: 'Account Settings',
        description: 'Manage your account',
        icon: 'Settings',
        articleCount: 0,
        color: '#8B5CF6',
      },
      {
        id: 'troubleshooting',
        name: 'Troubleshooting',
        description: 'Common issues and fixes',
        icon: 'AlertCircle',
        articleCount: 0,
        color: '#EF4444',
      },
    ],
    // Article + FAQ content now comes from the WP theme's db/v1 REST
    // routes via react-query hooks inside the page. This loader only
    // supplies the static category/search-suggestion metadata; empty
    // arrays are fine for the article stubs.
    articles: [],
    popularArticles: [],
    recentUpdates: [],
    faqs: [],
    searchSuggestions: [
      'How to place an order',
      'Payment methods',
      'Order tracking',
      'Refund policy',
      'Link building best practices',
    ],
    stats: {
      totalArticles: 0,
      totalViews: 0,
      averageRating: 0,
      responseTime: '< 24 hours',
    },
  };

  // Cache for 1 hour (help content doesn't change frequently)
  loaderCache.set(cacheKey, data, 60 * 60 * 1000);

  return data;
}

/**
 * Help Center loader function
 */
export async function helpCenterLoader(args: LoaderFunctionArgs) {
  const getElapsed = startRouteTimer();

  try {
    // Check authentication
    await requireAuth(args.request);

    // Get search params for filtering/searching
    const searchParams = getSearchParams(args);

    // Fetch help center data
    const data = await fetchHelpCenterData(searchParams);

    // Track performance
    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/help-center', loadTime, dataSize);

    // Return data directly (React Router loaders don't need to be wrapped)
    return data;
  } catch (error) {
    // If it's a redirect Response, let it propagate
    if (error instanceof Response) {
      throw error;
    }

    trackRouteError('/help-center', error as Error);
    throw error;
  }
}
